import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLevelAccess } from '../../context/LevelAccessContext';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, AIThinking, SectionHeader, Spinner, ProgressBar } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const API_BASE    = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TIMING      = { A1:65, A2:75, B1:90, B2:110 };
const SEC_META    = {
  hoeren:    { label:'Hören',    icon:'🎧', color:'blue' },
  lesen:     { label:'Lesen',    icon:'📖', color:'green' },
  schreiben: { label:'Schreiben',icon:'✍️', color:'gold' },
};
const SECTIONS = ['hoeren','lesen','schreiben'];

// ── Question answer inputs ─────────────────────────────────
function TrueFalseInput({ id, value, onChange }) {
  return (
    <div className="flex gap-3 mt-2">
      {['richtig','falsch'].map(opt=>(
        <label key={opt} className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 cursor-pointer text-sm font-semibold capitalize transition-all
          ${value===opt ? (opt==='richtig'?'border-green-400 bg-green-50 text-green-700':'border-brand-400 bg-brand-50 text-brand-700')
                       : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
          <input type="radio" name={id} value={opt} checked={value===opt} onChange={()=>onChange(opt)} className="sr-only"/>
          {opt==='richtig'?'✓':'✗'} {opt}
        </label>
      ))}
    </div>
  );
}

function MCInput({ id, value, onChange }) {
  return (
    <div className="space-y-1.5 mt-2">
      {['a','b','c','d'].map(opt=>(
        <label key={opt} className={`flex items-center gap-3 px-3 py-2 rounded-xl border-2 cursor-pointer text-sm transition-all
          ${value===opt?'border-brand-500 bg-brand-50 text-brand-800':'border-gray-100 hover:border-gray-300 text-gray-700'}`}>
          <input type="radio" name={id} value={opt} checked={value===opt} onChange={()=>onChange(opt)} className="sr-only"/>
          <span className="font-bold text-xs text-gray-400 w-4">{opt})</span>
          <span>Option {opt.toUpperCase()}</span>
        </label>
      ))}
    </div>
  );
}

function MatchingInput({ id, options, value, onChange }) {
  const opts = options && options.length>0 ? options : Array.from({length:8},(_,i)=>String.fromCharCode(97+i));
  return (
    <select value={value||''} onChange={e=>onChange(e.target.value)}
      className="mt-2 w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-brand-500 bg-white">
      <option value="">— Wählen Sie —</option>
      {opts.map(o=><option key={o} value={o}>{o.toUpperCase()}</option>)}
    </select>
  );
}

function GapFillInput({ id, wordList, value, onChange }) {
  if (wordList && wordList.length>0) {
    return (
      <select value={value||''} onChange={e=>onChange(e.target.value)}
        className="mt-2 w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-brand-500 bg-white">
        <option value="">— Wählen Sie —</option>
        {wordList.map(w=><option key={w} value={w}>{w}</option>)}
      </select>
    );
  }
  return (
    <input type="text" value={value||''} onChange={e=>onChange(e.target.value)}
      placeholder="Ihre Antwort…"
      className="mt-2 w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-brand-500"/>
  );
}

// ── Question block ─────────────────────────────────────────
function QuestionBlock({ sec, teil, qNum, type, wordList, value, onChange }) {
  const id  = `${sec}_${teil}_${qNum}`;
  const set = (v) => onChange(sec, teil, qNum, v);
  const prompts = {
    true_false: `Frage ${qNum} — Richtig oder Falsch?`,
    multiple_choice: `Frage ${qNum}`,
    matching: `Frage ${qNum} — Ordnen Sie zu`,
    gap_fill: `Lücke ${qNum}`,
  };
  return (
    <div className="border-t border-gray-100 pt-3 first:border-0 first:pt-0">
      <p className="text-sm font-medium text-de-black">{prompts[type] || `Frage ${qNum}`}</p>
      {type==='true_false'      && <TrueFalseInput id={id} value={value} onChange={set}/>}
      {type==='multiple_choice' && <MCInput id={id} value={value} onChange={set}/>}
      {type==='matching'        && <MatchingInput id={id} value={value} onChange={set}/>}
      {(type==='gap_fill'||type==='grammar_gap') && <GapFillInput id={id} wordList={wordList} value={value} onChange={set}/>}
    </div>
  );
}

// ── Audio player with play-count limit ────────────────────
function LimitedAudio({ src, maxPlays=2 }) {
  const [plays, setPlays] = useState(0);
  const [locked, setLocked] = useState(false);
  const ref = useRef();

  const handlePlay = () => {
    if (plays >= maxPlays - 1) setLocked(true);
    setPlays(p => p+1);
  };

  return (
    <div className="mb-4">
      <div className={`p-3 rounded-xl border-2 ${locked?'border-brand-200 bg-brand-50':'border-blue-200 bg-blue-50'}`}>
        {locked ? (
          <p className="text-xs font-semibold text-brand-600">🔒 Maximum plays reached ({maxPlays}×). In real Goethe exam, audio plays twice only.</p>
        ) : (
          <>
            <p className="text-xs text-blue-700 mb-2 font-medium">🎧 You can play this audio <strong>{maxPlays - plays}</strong> more time{maxPlays-plays!==1?'s':''}</p>
            <audio ref={ref} controls className="w-full" onPlay={handlePlay}
              src={`${API_BASE}/uploads/${src}`} />
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
export default function MockExamPage() {
  const { isLevelUnlocked } = useLevelAccess();
  const { addToast } = useToast();

  const [exams, setExams]             = useState([]);
  const [examsLoading, setExamsLoading] = useState(true);
  const [startingId, setStartingId]   = useState(null);

  // Active exam
  const [examDetail, setExamDetail]   = useState(null);
  const [attemptId, setAttemptId]     = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);

  // Student answers: { hoeren: { "1_1":"richtig", "1_2":"falsch" ... }, lesen:{...}, schreiben:{"1":"form...","2":"essay text"} }
  const [answers, setAnswers]         = useState({ hoeren:{}, lesen:{}, schreiben:{} });

  // Navigation
  const [activeSection, setActiveSection] = useState('hoeren');
  const [activeTeil, setActiveTeil]   = useState(1);

  // Timer
  const [timeLeft, setTimeLeft]       = useState(0);
  const timerRef = useRef(null);

  // Results
  const [aiLoading, setAiLoading]     = useState(false);
  const [results, setResults]         = useState(null);

  useEffect(() => {
    API.get('/exams')
      .then(({data}) => setExams(data.exams||[]))
      .catch(() => addToast('Failed to load exams','error'))
      .finally(() => setExamsLoading(false));
  }, []);

  useEffect(() => {
    if (!examDetail || timeLeft<=0) return;
    timerRef.current = setInterval(()=>{
      setTimeLeft(p=>{ if(p<=1){clearInterval(timerRef.current);handleSubmit();return 0;} return p-1; });
    }, 1000);
    return ()=>clearInterval(timerRef.current);
  }, [examDetail]);

  const startExam = async (exam) => {
    setStartingId(exam.id);
    try {
      const [{data:detail},{data:att}] = await Promise.all([
        API.get(`/exams/${exam.id}/full`),
        API.post(`/exams/${exam.id}/attempt`),
      ]);
      setExamDetail(detail.exam);
      setAttemptId(att.attemptId);
      setSelectedExam(exam);
      setTimeLeft((detail.exam.durationMinutes || TIMING[exam.level] || 60) * 60);
      setAnswers({hoeren:{},lesen:{},schreiben:{}});
      setActiveSection('hoeren');
      setActiveTeil(1);
      setResults(null);
    } catch(err) { addToast(err.message||'Failed to start exam','error'); }
    finally { setStartingId(null); }
  };

  const setAnswer = useCallback((sec, teil, qNum, val) => {
    const key = `${teil}_${qNum}`;
    setAnswers(p=>({...p,[sec]:{...p[sec],[key]:val}}));
  }, []);

  const setWriting = useCallback((teil, text) => {
    setAnswers(p=>({...p,schreiben:{...p.schreiben,[String(teil)]:text}}));
  }, []);

  const handleSubmit = async () => {
    if (!window.confirm('Are you sure you want to submit your exam? This cannot be undone.')) return;
    clearInterval(timerRef.current);
    setAiLoading(true);
    try {
      const {data} = await API.post(`/exams/${selectedExam.id}/attempt/${attemptId}/submit`, { answers });
      setResults(data);
    } catch(err) { addToast(err.message||'Submission failed','error'); }
    finally { setAiLoading(false); }
  };

  const formatTime = s => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
  const urgent     = timeLeft>0 && timeLeft<300;

  const getSectionTeile = (sec) => examDetail?.sections?.[sec] || [];
  const currentTeil     = getSectionTeile(activeSection).find(t=>t.teil===activeTeil) || getSectionTeile(activeSection)[0];

  // ── EXAM LIST ─────────────────────────────────────────────
  if (!selectedExam && !results) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <SectionHeader title="Mock Exams" subtitle="Practice with official Goethe-Institut exam structure"/>
        {examsLoading ? <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500"/></div>
        : exams.length===0 ? <p className="text-center text-gray-400 py-12">No exams published yet. Check back soon!</p>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {exams.map(exam=>{
              const unlocked = exam.isUnlocked;
              return (
                <div key={exam.id} className="relative">
                  <div className={`rounded-2xl border p-5 bg-white transition-all
                    ${unlocked?'border-gray-200 hover:border-brand-300 hover:shadow-lg cursor-pointer':'border-gray-200 cursor-not-allowed'}`}
                    onClick={()=>unlocked?startExam(exam):addToast(`Unlock Level ${exam.level} to access`,'warning')}>
                    {!unlocked&&(
                      <div className="absolute inset-0 rounded-2xl bg-gray-50/90 flex flex-col items-center justify-center z-10">
                        <span className="text-3xl mb-2">🔒</span>
                        <p className="text-sm font-medium text-gray-600">Unlock Level {exam.level}</p>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <Badge variant="gold">{exam.level}</Badge>
                      <span className="text-xs text-gray-400 font-medium">⏱ {TIMING[exam.level]||60} min</span>
                    </div>
                    <h3 className="font-semibold text-de-black text-sm mb-2">{exam.title}</h3>
                    <div className="flex gap-1.5 flex-wrap">
                      {Object.entries(SEC_META).map(([k,v])=>(
                        <span key={k} className="px-2 py-0.5 text-xs rounded-full bg-surface border border-gray-200 font-medium text-gray-600">
                          {v.icon} {v.label}
                        </span>
                      ))}
                    </div>
                    {startingId===exam.id&&(
                      <div className="absolute inset-0 rounded-2xl bg-white/80 flex items-center justify-center z-20">
                        <Spinner className="text-brand-500"/>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── MARKING SCREEN ────────────────────────────────────────
  if (aiLoading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">🤖</span>
        </div>
        <h2 className="text-xl font-bold text-de-black mb-2">Marking your exam…</h2>
        <p className="text-gray-500 text-sm mb-6">Hören & Lesen are being checked against the answer key. Claude AI is evaluating your Schreiben.</p>
        <AIThinking message="Calculating your Goethe exam score…"/>
      </div>
    );
  }

  // ── RESULTS ───────────────────────────────────────────────
  if (results) {
    const {scores, breakdown, aiFeedback} = results;
    const passed = scores?.passed;
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <span className="text-5xl mb-3 block">{passed?'🎓':'📚'}</span>
          <h1 className="font-display text-3xl font-bold text-de-black mb-1">{passed?'Bestanden!':'Nicht bestanden'}</h1>
          <p className="text-gray-500">{selectedExam.title}</p>
          <div className={`inline-flex items-center gap-2 mt-3 px-5 py-2 rounded-full text-sm font-bold
            ${passed?'bg-green-100 text-green-700':'bg-brand-50 text-brand-600'}`}>
            {passed?'✓ PASS':'✗ FAIL'} — {scores?.total}/{scores?.outOf} ({Math.round((scores?.total/scores?.outOf)*100)}%)
          </div>
          <p className="text-xs text-gray-400 mt-1">Pass mark: {scores?.passScore}/{scores?.outOf} (60%)</p>
        </div>

        <Card className="p-6 mb-4" accent="red">
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[['Hören','hoeren',scores?.hoeren],['Lesen','lesen',scores?.lesen],['Schreiben','schreiben',scores?.schreiben]].map(([label,key,score])=>(
              <div key={key} className={`p-4 rounded-xl text-center border-2 ${score>=15?'border-green-300 bg-green-50':'border-gray-200 bg-surface'}`}>
                <p className="text-xl font-bold text-de-black">{score}<span className="text-sm text-gray-400">/25</span></p>
                <p className="text-xs text-gray-500 font-medium mt-0.5">{SEC_META[key].icon} {label}</p>
              </div>
            ))}
          </div>

          {/* Schreiben AI feedback */}
          {aiFeedback && aiFeedback.length>0 && (
            <div className="space-y-3">
              {aiFeedback.map(fb=>(
                <div key={fb.teil} className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm">🤖</span>
                    <span className="text-xs font-bold text-purple-800">AI Feedback — Schreiben Teil {fb.teil} ({fb.score}/25)</span>
                  </div>
                  <p className="text-sm text-de-text">{fb.feedback}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="flex gap-3 justify-center">
          <Button variant="secondary" onClick={()=>{setSelectedExam(null);setResults(null);setExamDetail(null);}}>
            Back to Exams
          </Button>
          <Button variant="primary" onClick={()=>startExam(selectedExam)}>Retry</Button>
        </div>
      </div>
    );
  }

  // ── ACTIVE EXAM ───────────────────────────────────────────
  const sectionTeile = getSectionTeile(activeSection);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      {/* Timer bar */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs text-gray-400">Active exam</p>
          <h1 className="text-base font-bold text-de-black">{selectedExam.title}</h1>
        </div>
        <div className={`px-4 py-2 rounded-xl font-mono text-lg font-bold border-2 transition-colors
          ${urgent?'bg-brand-50 border-brand-400 text-brand-600 animate-pulse':'bg-surface border-gray-200 text-de-black'}`}>
          ⏱ {formatTime(timeLeft)}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 p-1 bg-surface rounded-2xl mb-3 overflow-x-auto">
        {SECTIONS.map(sec=>(
          <button key={sec} onClick={()=>{setActiveSection(sec);setActiveTeil(getSectionTeile(sec)[0]?.teil||1);}}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all flex-1 justify-center whitespace-nowrap
              ${activeSection===sec?'bg-white text-brand-500 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {SEC_META[sec].icon} {SEC_META[sec].label}
          </button>
        ))}
      </div>

      {/* Teil tabs */}
      {sectionTeile.length>1 && (
        <div className="flex gap-1 mb-4 overflow-x-auto">
          {sectionTeile.map(t=>(
            <button key={t.teil} onClick={()=>setActiveTeil(t.teil)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap
                ${activeTeil===t.teil?'bg-brand-500 text-white':'bg-surface text-gray-600 hover:bg-gray-100'}`}>
              Teil {t.teil}
            </button>
          ))}
        </div>
      )}

      {/* Section content */}
      {currentTeil && (
        <Card className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{SEC_META[activeSection].label} — Teil {currentTeil.teil}</p>
              {currentTeil.instructions && <p className="text-sm text-gray-700">{currentTeil.instructions}</p>}
            </div>
            <Badge variant={activeSection==='hoeren'?'info':activeSection==='lesen'?'completed':'gold'} size="xs">
              {currentTeil.questionCount||0} Fragen
            </Badge>
          </div>

          {/* Audio */}
          {activeSection==='hoeren' && currentTeil.contentFiles?.length>0 && (
            <LimitedAudio src={currentTeil.contentFiles[0]} maxPlays={2}/>
          )}
          {activeSection==='hoeren' && (!currentTeil.contentFiles||currentTeil.contentFiles.length===0) && (
            <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-xs text-blue-700">🎧 In der Prüfung hören Sie den Text hier. Beantworten Sie die Fragen basierend auf dem Hörtext.</p>
            </div>
          )}

          {/* Images */}
          {activeSection==='lesen' && currentTeil.contentFiles?.length>0 && (
            <div className="mb-4 space-y-2">
              {currentTeil.contentFiles.map((f,i)=>(
                <img key={i} src={`${API_BASE}/uploads/${f}`} alt={`Teil ${currentTeil.teil} image ${i+1}`}
                  className="w-full rounded-xl border border-gray-200 cursor-zoom-in max-h-80 object-contain bg-white"
                  onClick={e=>{e.target.requestFullscreen?.();}}/>
              ))}
              <p className="text-xs text-gray-400 text-center">Click image to zoom</p>
            </div>
          )}

          {/* Word list */}
          {currentTeil.wordList?.length>0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="text-xs text-gray-500 font-semibold self-center">Wortliste:</span>
              {currentTeil.wordList.map(w=>(
                <span key={w} className="px-2 py-1 bg-gold-50 border border-gold-400 rounded-lg text-xs font-medium text-de-black">{w}</span>
              ))}
            </div>
          )}

          {/* Questions */}
          {activeSection!=='schreiben' && (
            <div className="space-y-4">
              {Array.from({length:currentTeil.questionCount||0},(_,i)=>{
                const qNum = i+1;
                const key  = `${currentTeil.teil}_${qNum}`;
                return (
                  <QuestionBlock key={qNum}
                    sec={activeSection} teil={currentTeil.teil} qNum={qNum}
                    type={currentTeil.questionType}
                    wordList={currentTeil.wordList||[]}
                    value={answers[activeSection]?.[key]||''}
                    onChange={setAnswer}/>
                );
              })}
            </div>
          )}

          {/* Schreiben */}
          {activeSection==='schreiben' && (
            <div>
              {currentTeil.questionType==='form_fill' ? (
                <div>
                  {currentTeil.contentFiles?.length>0 && (
                    <img src={`${API_BASE}/uploads/${currentTeil.contentFiles[0]}`} alt="Form" className="w-full rounded-xl border border-gray-200 mb-4"/>
                  )}
                  <p className="text-xs text-gray-500 mb-2">Fill in the form above with your personal details in the answer box below:</p>
                  <textarea value={answers.schreiben?.[String(currentTeil.teil)]||''} rows={4}
                    onChange={e=>setWriting(currentTeil.teil,e.target.value)}
                    placeholder="Vorname: ..., Nachname: ..., Alter: ..., ..."
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-brand-500 resize-none"/>
                </div>
              ) : (
                <div>
                  {currentTeil.writingPrompt && (
                    <div className="mb-4 p-4 bg-gold-50 rounded-xl border border-gold-400">
                      <p className="text-xs font-bold text-de-black mb-1">Aufgabe:</p>
                      <p className="text-sm text-de-text leading-relaxed">{currentTeil.writingPrompt}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">{currentTeil.minWords||80}–{currentTeil.maxWords||150} Wörter</p>
                    {(()=>{
                      const text = answers.schreiben?.[String(currentTeil.teil)]||'';
                      const wc   = text.trim().split(/\s+/).filter(Boolean).length;
                      const min  = currentTeil.minWords||80;
                      const max  = currentTeil.maxWords||150;
                      const ok   = wc>=min&&wc<=max;
                      return <span className={`text-xs font-bold px-2 py-1 rounded-full ${ok?'bg-green-100 text-green-700':'bg-brand-50 text-brand-600'}`}>{wc} Wörter</span>;
                    })()}
                  </div>
                  <textarea
                    value={answers.schreiben?.[String(currentTeil.teil)]||''} rows={12}
                    onChange={e=>setWriting(currentTeil.teil,e.target.value)}
                    placeholder="Schreiben Sie hier Ihren Text…"
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-brand-500 resize-none font-sans leading-relaxed"/>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Bottom bar */}
      <div className="mt-5 flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl sticky bottom-4 shadow-xl">
        <div className="text-sm text-gray-600">
          <span className="font-bold text-brand-500">
            {Object.values(answers).flatMap(sec=>Object.values(sec)).filter(v=>v).length}
          </span> answers saved
        </div>
        <Button variant="primary" size="lg" onClick={handleSubmit}>Prüfung abgeben →</Button>
      </div>
    </div>
  );
}
